import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import Skeleton from '../Skeleton';
import CarCarouselCardSkeleton from './CarCarouselCardSkeleton';
import { Colors, DesignTokens } from '../../styles/colors';
import { SCREENS } from '../responsive';
import { getSiteConfig } from '../../constants/siteConfig';

const PageContainer = styled.div`
  ${tw`w-full max-w-full min-w-0 h-full flex flex-col overflow-x-hidden items-stretch box-border pb-8 md:pb-16`}
`;

const ContentMaxWidth = styled.div`
  ${tw`w-full mx-auto px-2 md:px-4 lg:px-0 box-border max-w-full min-w-0`}
  max-width: ${DesignTokens.container.maxWidth};
`;

// —— Hero (matches topSection.tsx) ——
const TopSectionWrapper = styled.div`
  ${tw`w-full max-w-full min-w-0 overflow-x-hidden box-border`}
  background: ${Colors.BgPrimary};
`;

const TopSectionOuter = styled(ContentMaxWidth)`
  ${tw`pt-2 md:pt-4`}
`;

const HeroBanner = styled.div`
  ${tw`
    w-full
    relative
    flex
    flex-col
    items-center
    box-border
    overflow-hidden
  `}
  border-radius: 16px;

  @media (min-width: ${SCREENS.md}) {
    border-radius: 24px;
  }
`;

const HeroBannerFill = styled(Skeleton)`
  ${tw`absolute inset-0`}
  border-radius: inherit;
`;

const HeroContent = styled.div`
  ${tw`
    w-full
    flex
    flex-col
    items-center
    text-center
    gap-6
    relative
    z-10
  `}
  padding: 24px 16px 28px;

  @media (min-width: ${SCREENS.md}) {
    padding: 56px 40px 48px;
    gap: 8px;
  }

  @media (min-width: ${SCREENS.lg}) {
    padding: 64px 56px 56px;
  }
`;

const HeroTextBlock = styled.div`
  ${tw`w-full flex flex-col items-center max-w-2xl`}
`;

const HeroTitleSkeleton = styled(Skeleton)`
  ${tw`h-9 w-56 md:h-12 md:w-80 mb-3 md:mb-4`}
  border-radius: 8px;
`;

const HeroSubtitleSkeleton = styled(Skeleton)`
  ${tw`h-4 w-full max-w-md mb-2 md:mb-6`}
`;

const SearchCardWrapper = styled.div`
  ${tw`w-full`}

  @media (min-width: ${SCREENS.lg}) {
    max-width: 900px;
  }
`;

const SearchBarSkeleton = styled(Skeleton)`
  ${tw`w-full`}
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
`;

const SearchBarMobile = styled(SearchBarSkeleton)`
  ${tw`lg:hidden`}
  min-height: 56px;
`;

const SearchBarDesktop = styled.div`
  ${tw`hidden lg:flex items-stretch w-full overflow-hidden`}
  max-width: 900px;
  min-height: 54px;
  height: 54px;
  background: ${Colors.White};
  border: 1px solid ${Colors.BorderLight};
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
`;

const DesktopSegment = styled.div<{ $wide?: boolean }>`
  ${tw`flex flex-col justify-center flex-1 min-w-0 px-4 py-2 gap-1`}
  ${props => props.$wide && tw`flex-[1.08]`}
  border-right: 1px solid ${Colors.BorderLight};
`;

const SegmentLabelSkeleton = styled(Skeleton)`
  ${tw`h-3 w-10`}
`;

const SegmentValueSkeleton = styled(Skeleton)`
  ${tw`h-4 w-full max-w-[140px]`}
`;

const SearchActionSkeleton = styled(Skeleton)`
  ${tw`flex-shrink-0 m-[5px] self-center`}
  width: 44px;
  height: 44px;
  border-radius: 8px;
`;

// —— Category filter ——
const CategoryFilterRow = styled(ContentMaxWidth)`
  ${tw`
    flex
    items-center
    overflow-x-auto
    py-4
    space-x-3
  `}
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const CategoryPillSkeleton = styled(Skeleton)`
  ${tw`flex-shrink-0`}
  width: 88px;
  height: 40px;
  border-radius: 8px;
`;

const CategoryPillWideSkeleton = styled(CategoryPillSkeleton)`
  width: 72px;
`;

// —— Car carousels (matches CarsSlideShow loading state) ——
const TopCarsOuter = styled(ContentMaxWidth)`
  ${tw`flex flex-col items-start mb-10`}
`;

const CarouselSection = styled.div`
  ${tw`w-full flex flex-col`}
`;

const CarouselHeader = styled.div`
  ${tw`
    flex
    w-full
    justify-between
    items-center
    mb-6
    px-4
    md:px-0
  `}
`;

const CarouselTitleSkeleton = styled(Skeleton)`
  ${tw`h-7 w-40 md:h-8 md:w-52`}
  border-radius: 6px;
`;

const CarouselArrows = styled.div`
  ${tw`flex items-center`}
  gap: 12px;
`;

const ArrowSkeleton = styled(Skeleton)`
  width: 40px;
  height: 40px;
  border-radius: 8px;
`;

const CarouselTrack = styled.div`
  ${tw`
    w-full
    flex
    overflow-x-hidden
    pb-4
    px-4
    md:px-0
  `}
  gap: 16px;
`;

const SectionGap = styled.div`
  height: 32px;
`;

const CATEGORY_PILL_COUNT = 5;
const CAROUSEL_CARD_COUNT = 4;

const CarouselSectionSkeleton: React.FC<{ titleWidth?: string }> = ({ titleWidth = '10rem' }) => (
  <CarouselSection aria-hidden>
    <CarouselHeader>
      <CarouselTitleSkeleton width={titleWidth} />
      <CarouselArrows>
        <ArrowSkeleton />
        <ArrowSkeleton />
      </CarouselArrows>
    </CarouselHeader>
    <CarouselTrack>
      {Array.from({ length: CAROUSEL_CARD_COUNT }, (_, index) => (
        <CarCarouselCardSkeleton key={index} />
      ))}
    </CarouselTrack>
  </CarouselSection>
);

const HomePageSkeleton: React.FC = () => {
  const showCategoryFilter = !getSiteConfig().allowedProductTypes;

  return (
    <PageContainer aria-busy="true" aria-label="Home page loading">
      <TopSectionWrapper>
        <TopSectionOuter>
          <HeroBanner>
            <HeroBannerFill />
            <HeroContent>
              <HeroTextBlock>
                <HeroTitleSkeleton />
                <HeroSubtitleSkeleton />
                <HeroSubtitleSkeleton width="75%" />
              </HeroTextBlock>
              <SearchCardWrapper>
                <SearchBarMobile />
                <SearchBarDesktop>
                  <DesktopSegment $wide>
                    <SegmentLabelSkeleton />
                    <SegmentValueSkeleton />
                  </DesktopSegment>
                  <DesktopSegment>
                    <SegmentLabelSkeleton />
                    <SegmentValueSkeleton width="6rem" />
                  </DesktopSegment>
                  <DesktopSegment>
                    <SegmentLabelSkeleton />
                    <SegmentValueSkeleton width="6rem" />
                  </DesktopSegment>
                  <SearchActionSkeleton />
                </SearchBarDesktop>
              </SearchCardWrapper>
            </HeroContent>
          </HeroBanner>
        </TopSectionOuter>
      </TopSectionWrapper>

      {showCategoryFilter && (
        <CategoryFilterRow>
          <CategoryPillWideSkeleton />
          {Array.from({ length: CATEGORY_PILL_COUNT - 1 }, (_, index) => (
            <CategoryPillSkeleton key={index} />
          ))}
        </CategoryFilterRow>
      )}

      <TopCarsOuter>
        <CarouselSectionSkeleton titleWidth="11rem" />
        <SectionGap />
        <CarouselSectionSkeleton titleWidth="14rem" />
      </TopCarsOuter>
    </PageContainer>
  );
};

export default HomePageSkeleton;
